import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { 
  Calendar, MapPin, Clock, Users, Lock, Star, ExternalLink,
  Tag, Gift, Building, ChevronRight, Filter, ArrowLeft, Sparkles, Ticket
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import EventPhotoDisplay from '@/components/EventPhotoDisplay';
import api from '@/lib/api';

const CITIES = [
  'Los Angeles', 'Porter Ranch', 'San Diego', 'San Francisco', 'Bay Area', 'Sacramento',
  'Orange County', 'San Jose', 'Santa Barbara', 'Palm Springs', 'Other California'
];

const BUSINESS_CATEGORIES = [
  { value: 'all', label: 'All Categories' },
  { value: 'kids_activities', label: 'Kids Activities' },
  { value: 'wellness', label: 'Wellness & Spa' },
  { value: 'restaurants', label: 'Restaurants' },
  { value: 'fitness', label: 'Fitness' },
  { value: 'services', label: 'Services' },
  { value: 'shopping', label: 'Shopping' }
];

export default function MyCityPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = ['community', 'workshops', 'exclusive', 'businesses'].includes(searchParams.get('tab'))
    ? searchParams.get('tab')
    : 'community';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [events, setEvents] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [cityFilter, setCityFilter] = useState(user?.city || 'all_cities');
  const [businessCategory, setBusinessCategory] = useState('all');
  
  // Discount modal
  const [discountModal, setDiscountModal] = useState({ open: false, business: null });

  useEffect(() => {
    fetchData();
  }, [activeTab, cityFilter]);

  const handleTabChange = (v) => {
    setActiveTab(v);
    setSearchParams(v === 'community' ? {} : { tab: v }, { replace: true });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab !== 'businesses') {
        const response = await api.get('/events');
        let filteredEvents = response.data || [];
        
        // Filter by city
        if (cityFilter && cityFilter !== 'all_cities') {
          filteredEvents = filteredEvents.filter(e => e.city === cityFilter);
        }
        
        // Filter by event type
        if (activeTab === 'exclusive') {
          filteredEvents = filteredEvents.filter(e => e.is_exclusive);
        }
        
        setEvents(filteredEvents);
      } else {
        const response = await api.get('/businesses');
        let filteredBusinesses = response.data?.businesses || [];
        
        // Filter by city
        if (cityFilter && cityFilter !== 'all_cities') {
          filteredBusinesses = filteredBusinesses.filter(b => b.city === cityFilter);
        }
        
        // Filter by category
        if (businessCategory && businessCategory !== 'all') {
          filteredBusinesses = filteredBusinesses.filter(b => b.category === businessCategory);
        }
        
        setBusinesses(filteredBusinesses);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRSVP = async (eventId) => {
    try {
      const response = await api.post(`/events/${eventId}/rsvp`);
      setEvents(events.map(e => 
        e.id === eventId ? { ...e, user_rsvped: response.data.rsvped } : e
      ));
      toast.success(response.data.rsvped ? "You're in!" : 'RSVP cancelled');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to RSVP');
    }
  };

  const formatEventDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const canAccessExclusive = user?.membership_tier !== 'free';

  return (
    <div className="min-h-screen bg-cream font-body">
      {/* Header */}
      <header className="bg-white border-b border-border-subtle sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="font-display text-xl text-navy">
            Cali<span className="italic-teal">Moms</span> Connect
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/dashboard" className="text-sm text-text-mid hover:text-teal">Dashboard</Link>
            <Link to="/community" className="text-sm text-text-mid hover:text-teal">Community Board</Link>
            <span className="text-sm text-teal font-semibold">My City</span>
            <Link to="/wellness" className="text-sm text-text-mid hover:text-teal" data-testid="wellness-nav-link">Wellness</Link>
          </nav>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Page Title */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 rounded-full bg-white border border-border-subtle flex items-center justify-center text-navy hover:bg-seafoam/30 hover:text-teal transition-colors shadow-sm"
              aria-label="Back"
              data-testid="my-city-back-btn"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="font-display text-3xl md:text-4xl text-navy mb-2">My City</h1>
              <p className="text-text-mid">Events, exclusive gatherings, and local deals</p>
            </div>
          </div>
          
          {/* City Filter */}
          <Select value={cityFilter} onValueChange={setCityFilter}>
            <SelectTrigger className="w-48 rounded-xl border-seafoam bg-white">
              <MapPin size={16} className="mr-2 text-teal" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all_cities">All Cities</SelectItem>
              {CITIES.map(c => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <TabsList className="bg-white rounded-full p-1 border border-border-subtle">
            <TabsTrigger 
              value="community" 
              className="rounded-full px-6 data-[state=active]:bg-teal data-[state=active]:text-white"
              data-testid="tab-community-events"
            >
              Community Events
            </TabsTrigger>
            <TabsTrigger 
              value="workshops" 
              className="rounded-full px-6 data-[state=active]:bg-teal data-[state=active]:text-white"
              data-testid="tab-workshops"
            >
              <Sparkles size={14} className="mr-1" />
              Workshops
            </TabsTrigger>
            <TabsTrigger 
              value="exclusive" 
              className="rounded-full px-6 data-[state=active]:bg-teal data-[state=active]:text-white"
              data-testid="tab-exclusive-events"
            >
              <Star size={14} className="mr-1" />
              Exclusive Events
            </TabsTrigger>
            <TabsTrigger 
              value="businesses" 
              className="rounded-full px-6 data-[state=active]:bg-teal data-[state=active]:text-white"
              data-testid="tab-businesses"
            >
              <Gift size={14} className="mr-1" />
              Local Deals
            </TabsTrigger>
          </TabsList>

          {/* Community Events Tab — only gatherings, workshops moved to their own tab */}
          <TabsContent value="community" className="space-y-6">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-pulse text-teal">Loading events...</div>
              </div>
            ) : events.filter(e => (e.event_type || 'gathering').toLowerCase() !== 'workshop').length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl">
                <Calendar className="mx-auto text-text-light mb-4" size={40} />
                <p className="text-text-mid">No upcoming gatherings in this area</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {events
                  .filter(e => (e.event_type || 'gathering').toLowerCase() !== 'workshop')
                  .map(event => (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-2xl overflow-hidden shadow-card hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => navigate(`/events/${event.id}`)}
                    data-testid={`event-${event.id}`}
                  >
                    <EventPhotoDisplay event={event} variant="card" testIdPrefix={`community-event-${event.id}`} />
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <Badge className="bg-teal/10 text-teal text-xs">Community Event</Badge>
                      </div>
                      <h3 className="font-semibold text-navy mb-2">{event.title}</h3>
                      <div className="space-y-1 text-sm text-text-mid mb-4">
                        <p className="flex items-center gap-2">
                          <Clock size={14} className="text-teal" />
                          {formatEventDate(event.event_date)}
                        </p>
                        <p className="flex items-center gap-2">
                          <MapPin size={14} className="text-teal" />
                          {event.location} • {event.city}
                        </p>
                        <p className="flex items-center gap-2">
                          <Users size={14} className="text-teal" />
                          {event.rsvp_count || 0} / {event.max_capacity} attending
                        </p>
                      </div>
                      <Button
                        onClick={(e) => { e.stopPropagation(); handleRSVP(event.id); }}
                        variant={event.user_rsvped ? "outline" : "default"}
                        className={`w-full rounded-full ${
                          event.user_rsvped 
                            ? 'border-teal text-teal' 
                            : 'bg-teal hover:bg-teal-deep text-white'
                        }`}
                        data-testid={`rsvp-${event.id}`}
                      >
                        {event.user_rsvped ? "Cancel RSVP" : "RSVP"}
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Workshops Tab (Phase 2) — dedicated workshop cards with tier-aware CTA */}
          <TabsContent value="workshops" className="space-y-6">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-pulse text-teal">Loading workshops...</div>
              </div>
            ) : events.filter(e => (e.event_type || '').toLowerCase() === 'workshop').length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl">
                <Sparkles className="mx-auto text-text-light mb-4" size={40} />
                <p className="font-display text-lg text-navy mb-1">No workshops scheduled yet</p>
                <p className="text-text-mid text-sm">New workshops go live monthly. Thrive members attend one free every month.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {events
                  .filter(e => (e.event_type || '').toLowerCase() === 'workshop')
                  .map(event => {
                    const w = event.workshop || {};
                    const base = Number(w.base_price || event.base_price || 0);
                    const label = w.pricing_label;
                    let ctaText = `Register — $${base.toFixed(0)}`;
                    let ctaClass = 'bg-navy hover:bg-navy/90 text-white';
                    if (label === 'workshop_free_credit') {
                      ctaText = 'Register with credit — free';
                      ctaClass = 'bg-teal hover:bg-teal-deep text-white';
                    } else if (label === 'workshop_member_30off') {
                      const disc = (base * 0.7).toFixed(2);
                      ctaText = `Register — $${disc} (member)`;
                      ctaClass = 'bg-navy hover:bg-navy/90 text-white';
                    }
                    return (
                      <motion.div
                        key={event.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white rounded-2xl overflow-hidden shadow-card hover:shadow-lg transition-shadow cursor-pointer"
                        onClick={() => navigate(`/events/${event.id}`)}
                        data-testid={`workshop-${event.id}`}
                      >
                        <EventPhotoDisplay event={event} variant="card" testIdPrefix={`workshop-${event.id}`} />
                        <div className="p-4">
                          <div className="flex items-start justify-between mb-2 gap-2">
                            <Badge className="bg-sand/40 text-navy text-xs border border-sand">
                              <Sparkles size={10} className="mr-1" /> Workshop
                            </Badge>
                            {event.host_name && (
                              <span className="text-[11px] text-text-mid italic">w/ {event.host_name}</span>
                            )}
                          </div>
                          <h3 className="font-semibold text-navy mb-2">{event.title}</h3>
                          <div className="space-y-1 text-sm text-text-mid mb-4">
                            <p className="flex items-center gap-2">
                              <Clock size={14} className="text-teal" />
                              {formatEventDate(event.event_date)}
                            </p>
                            <p className="flex items-center gap-2">
                              <MapPin size={14} className="text-teal" />
                              {event.location} • {event.city}
                            </p>
                            <p className="flex items-center gap-2">
                              <Users size={14} className="text-teal" />
                              {event.rsvp_count || 0} / {event.max_capacity} spots
                            </p>
                          </div>
                          <Button
                            onClick={(e) => { e.stopPropagation(); navigate(`/events/${event.id}`); }}
                            className={`w-full rounded-full ${ctaClass}`}
                            data-testid={`workshop-cta-${event.id}`}
                          >
                            <Ticket size={14} className="mr-2" />
                            {ctaText}
                          </Button>
                        </div>
                      </motion.div>
                    );
                  })}
              </div>
            )}
          </TabsContent>

          {/* Exclusive Events Tab */}
          <TabsContent value="exclusive" className="space-y-6">
            {!canAccessExclusive ? (
              <div className="text-center py-12 bg-white rounded-2xl">
                <Lock className="mx-auto text-text-light mb-4" size={40} />
                <h3 className="font-display text-xl text-navy mb-2">Exclusive Events</h3>
                <p className="text-text-mid mb-4">Thrive includes one workshop free every month — less than the price of a single workshop. Plus exclusive member events and gatherings.</p>
                <Link to="/pricing">
                  <Button className="bg-teal hover:bg-teal-deep text-white rounded-full">
                    Upgrade to Access
                  </Button>
                </Link>
              </div>
            ) : loading ? (
              <div className="text-center py-12">
                <div className="animate-pulse text-teal">Loading exclusive events...</div>
              </div>
            ) : events.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl">
                <Star className="mx-auto text-text-light mb-4" size={40} />
                <p className="text-text-mid">No exclusive events scheduled</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {events.map(event => (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-2xl overflow-hidden shadow-card hover:shadow-lg transition-shadow cursor-pointer border-2 border-teal/20"
                    onClick={() => navigate(`/events/${event.id}`)}
                    data-testid={`exclusive-event-${event.id}`}
                  >
                    <EventPhotoDisplay event={event} variant="card" testIdPrefix={`exclusive-event-${event.id}`} />
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <Badge className="bg-gradient-to-r from-teal to-teal-deep text-white text-xs">
                          <Star size={10} className="mr-1" /> Exclusive
                        </Badge>
                      </div>
                      <h3 className="font-semibold text-navy mb-2">{event.title}</h3>
                      <div className="space-y-1 text-sm text-text-mid mb-4">
                        <p className="flex items-center gap-2">
                          <Clock size={14} className="text-teal" />
                          {formatEventDate(event.event_date)}
                        </p>
                        <p className="flex items-center gap-2">
                          <MapPin size={14} className="text-teal" />
                          {event.location} • {event.city}
                        </p>
                        <p className="flex items-center gap-2">
                          <Users size={14} className="text-teal" />
                          {event.rsvp_count || 0} / {event.max_capacity} spots
                        </p>
                      </div>
                      <Button
                        onClick={(e) => { e.stopPropagation(); handleRSVP(event.id); }}
                        variant={event.user_rsvped ? "outline" : "default"}
                        className={`w-full rounded-full ${
                          event.user_rsvped 
                            ? 'border-teal text-teal' 
                            : 'bg-teal hover:bg-teal-deep text-white'
                        }`}
                      >
                        {event.user_rsvped ? "Cancel RSVP" : "Reserve Your Spot"}
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Local Businesses Tab */}
          <TabsContent value="businesses" className="space-y-6">
            {/* Category Filter */}
            <div className="flex gap-4">
              <Select value={businessCategory} onValueChange={setBusinessCategory}>
                <SelectTrigger className="w-48 rounded-xl border-seafoam bg-white">
                  <Filter size={16} className="mr-2 text-teal" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BUSINESS_CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-pulse text-teal">Loading local deals...</div>
              </div>
            ) : businesses.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl">
                <Building className="mx-auto text-text-light mb-4" size={40} />
                <p className="text-text-mid mb-4">No business partners in this area yet</p>
                <a 
                  href="mailto:partnerships@calimoms.co?subject=Business Partnership Inquiry"
                  className="text-teal font-semibold hover:text-teal-deep"
                >
                  Become a Partner →
                </a>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {businesses.map(business => (
                  <motion.div
                    key={business.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-2xl overflow-hidden shadow-card hover:shadow-lg transition-shadow"
                    data-testid={`business-${business.id}`}
                  >
                    <div className="h-24 bg-seafoam/20 flex items-center justify-center p-4">
                      {business.logo_url ? (
                        <img src={business.logo_url} alt={business.name} className="max-h-full max-w-full object-contain" />
                      ) : (
                        <Building className="text-text-light" size={32} />
                      )}
                    </div>
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-navy">{business.name}</h3>
                        {business.discount_code && (
                          <Badge className="bg-teal/10 text-teal text-xs">
                            <Gift size={10} className="mr-1" /> Deal
                          </Badge>
                        )}
                      </div>
                      <Badge variant="outline" className="text-xs mb-2">{business.category_label || business.category}</Badge>
                      <p className="text-sm text-text-mid mb-3 line-clamp-2">{business.description}</p>
                      <p className="text-xs text-text-light flex items-center gap-1 mb-4">
                        <MapPin size={12} /> {business.city}
                      </p>
                      
                      <div className="flex gap-2">
                        {business.discount_code && (
                          <Button
                            onClick={() => setDiscountModal({ open: true, business })}
                            className="flex-1 bg-teal hover:bg-teal-deep text-white rounded-full text-sm"
                          >
                            <Gift size={14} className="mr-1" /> View Deal
                          </Button>
                        )}
                        {business.website_url && (
                          <Button
                            variant="outline"
                            size="icon"
                            className="rounded-full border-seafoam"
                            onClick={() => window.open(business.website_url, '_blank')}
                          >
                            <ExternalLink size={16} />
                          </Button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            {/* Become a Partner CTA */}
            <div className="bg-gradient-to-r from-teal to-teal-deep rounded-2xl p-8 text-center text-white">
              <h3 className="font-display text-2xl mb-2">Own a Local Business?</h3>
              <p className="mb-4 text-white/80">Partner with Cali Moms Connect to reach thousands of local moms</p>
              <a href="mailto:partnerships@calimoms.co?subject=Business Partnership Inquiry">
                <Button className="bg-white text-teal hover:bg-cream rounded-full">
                  Become a Partner <ChevronRight size={16} className="ml-1" />
                </Button>
              </a>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Discount Modal */}
      <Dialog open={discountModal.open} onOpenChange={(open) => !open && setDiscountModal({ open: false, business: null })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-navy flex items-center gap-2">
              <Gift className="text-teal" size={20} />
              Member Exclusive Deal
            </DialogTitle>
          </DialogHeader>
          {discountModal.business && (
            <div className="space-y-4">
              <div className="text-center">
                <h3 className="font-semibold text-lg text-navy">{discountModal.business.name}</h3>
                <p className="text-text-mid text-sm">{discountModal.business.city}</p>
              </div>
              
              <div className="bg-cream rounded-xl p-4 text-center">
                <p className="text-sm text-text-mid mb-2">{discountModal.business.discount_description || "Special discount for Cali Moms members"}</p>
                <div className="bg-white border-2 border-dashed border-teal rounded-lg p-3">
                  <p className="text-xs text-text-light mb-1">Use code:</p>
                  <p className="font-mono text-xl font-bold text-teal">{discountModal.business.discount_code}</p>
                </div>
              </div>
              
              <Badge className="w-full justify-center bg-teal/10 text-teal py-2">
                <Star size={14} className="mr-2" /> Member Exclusive
              </Badge>
              
              {discountModal.business.website_url && (
                <Button
                  onClick={() => window.open(discountModal.business.website_url, '_blank')}
                  className="w-full bg-teal hover:bg-teal-deep text-white rounded-full"
                >
                  Visit Website <ExternalLink size={14} className="ml-2" />
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
